import React, { memo } from 'react';
import {
  Button,
  IconInfo,
  IconWarning,
  Tooltip,
} from 'gatorcomponents';
import * as Styles from '../../ServerList.styles';
import { useIsMobile } from '@/hooks/useIsMobile';
import ServerStatus from '../ServerStatus';
import ServerNickname from '../ServerNickname';
import FavoriteButton from '../FavoriteButton';
import QuickAccess from '../QuickAccess';
import useLocale from '@/hooks/useLocale';
import useServerStatus from '@/hooks/useServerStatus';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';

const ServerListItem = memo(({
  server,
  onGoToManagement,
  onUpdateNickname,
  onFavoriteUpdateSuccess,
}) => {
  const isMobile = useIsMobile();
  const { productDetails: productDetailsLocale, routes: localeRoutes } = useLocale();
  const { advancedHosting: advancedHostingLocale } = productDetailsLocale || {};
  const { serverList: serverListLocale } = advancedHostingLocale || {};

  const handleManagementClick = () => {
    onGoToManagement(server.id);
  };

  const handlePaymentClick = (invoiceId) => {
    const url = `${localeRoutes.billing}${localeRoutes.unpaid}/${invoiceId}?openInvoice=true`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const serverInfo = server?.serverInfo || {};
  const serverId = server?.id;
  const serverPackageId = server?.packageid;
  const serverDomainStatus = server?.domainstatus;

  const applications = server?.applications || [];
  const isVpsN8NProduct = HgProducts.VPS_N8N_ALL.includes(serverPackageId);
  if (isVpsN8NProduct && !applications.includes('n8n')) {
    applications.push('n8n');
  }

  const isVpsCpanelProduct = HgProducts.VPS_CPANEL_ALL.includes(serverPackageId);
  if (isVpsCpanelProduct && !applications.includes('cpanel')) {
    applications.push('cpanel');
  }

  const {
    serverStatus,
    serverStatusLoading,
    serverDisabledFeatures,
    hasError,
  } = useServerStatus({
    serverInfo,
    serverId,
    serverPackageId,
    serverDomainStatus,
  });

  const isStatusPending = serverStatus === VpsOciStatusEnum.PENDING;
  const isStatusSuspended = serverStatus === VpsOciStatusEnum.SUSPENDED;
  const isStatusBuilding = serverStatus === VpsOciStatusEnum.BUILDING;
  const isStatusOffline = serverStatus === VpsOciStatusEnum.OFFLINE;

  const disabledButtonManagement = serverDisabledFeatures && !isStatusOffline;
  const enableQuickAccess = !serverDisabledFeatures && !serverStatusLoading && !hasError;

  return (
    <Styles.ListRow>
      <Styles.ListItem data-testid={`server-list-item-${server.id}`}>
        <Styles.Cell>
          <Styles.CellContent>
            <Styles.ServerName data-testid={`server-product-name-${server.id}`}>
              {server.productName}
            </Styles.ServerName>
            <Styles.ServerDomain data-testid={`server-domain-${server.id}`}>{server.domain}</Styles.ServerDomain>
            <ServerNickname
              serverId={server.id}
              nickname={server.nickName}
              placeholder={serverListLocale?.columnsLabel?.setServerNickname}
              onUpdateNickname={onUpdateNickname}
            />
          </Styles.CellContent>
          {isMobile && (
            <FavoriteButton
              serverId={server.id}
              isFavorite={server.isFavorite}
              locale={serverListLocale?.tooltip}
              onSuccess={onFavoriteUpdateSuccess}
            />
          )}
        </Styles.Cell>

        <Styles.Cell>
          <Styles.CellContent>
            {isMobile && <Styles.CellLabel>{serverListLocale?.columnsLabel?.addressIp}</Styles.CellLabel>}
            <Styles.ServerIp data-testid={`server-ip-${server.id}`}>{server.ip}</Styles.ServerIp>
          </Styles.CellContent>
          {isMobile && <ServerStatus loading={serverStatusLoading} status={serverStatus} />}
        </Styles.Cell>

        {!isMobile && (
          <Styles.Cell>
            <ServerStatus
              loading={serverStatusLoading}
              status={serverStatus}
            />
          </Styles.Cell>
        )}

        <Styles.Cell>
          {enableQuickAccess && (
            <Styles.CellContent>
              {(isMobile && applications.length > 0) && (
                <Styles.CellLabel>{serverListLocale?.columnsLabel?.quickAccess}</Styles.CellLabel>
              )}
              <QuickAccess
                locale={serverListLocale}
                applications={applications}
                productId={server.id}
                serverIp={server.ip}
                domain={server.domain}
              />
            </Styles.CellContent>
          )}
        </Styles.Cell>

        <Styles.ActionsGroup align="right">
          <Button
            label={serverListLocale?.buttons?.management}
            onClick={handleManagementClick}
            variant="secondary"
            width={isMobile ? 'full' : 'fit-content'}
            size="large"
            version="v2"
            testId={`server-management-button-${server.id}`}
            disabled={disabledButtonManagement}
          />
          {!isMobile && (
            <Tooltip content={serverListLocale?.tooltip?.favorite} preferredPosition="bottomRight">
              <FavoriteButton
                serverId={server.id}
                isFavorite={server.isFavorite}
                locale={serverListLocale?.tooltip}
                onSuccess={onFavoriteUpdateSuccess}
              />
            </Tooltip>
          )}
        </Styles.ActionsGroup>
      </Styles.ListItem>

      {isStatusPending && (
        <Styles.ListItemAlertDanger data-testid={`server-list-item-alert-${server.id}`}>
          <Styles.DangerLabel>
            <IconWarning size="sm2" color="attentionExtraDark" />

            <Styles.AlertText>
              <Styles.AlertMessage>
                {serverListLocale?.alertMessages?.activateServer}
              </Styles.AlertMessage>

              {server.invoiceid && (
                <Styles.AlertLink
                  data-testid={`finish-payment-link-${server.invoiceid}`}
                  onClick={() => handlePaymentClick(server.invoiceid)}
                >
                  {serverListLocale?.alertMessages?.paymentPending}
                </Styles.AlertLink>
              )}
            </Styles.AlertText>
          </Styles.DangerLabel>
        </Styles.ListItemAlertDanger>
      )}

      {isStatusSuspended && (
        <Styles.ListItemAlertDanger data-testid={`server-list-item-alert-${server.id}`}>
          <Styles.DangerLabel>
            <IconWarning size="sm2" color="attentionExtraDark" />

            <Styles.AlertText>
              <Styles.AlertMessage>
                {serverListLocale?.alertMessages?.reactiveServer}
              </Styles.AlertMessage>

              {server.invoiceid && (
                <Styles.AlertLink
                  data-testid={`finish-payment-link-${server.invoiceid}`}
                  onClick={() => handlePaymentClick(server.invoiceid)}
                >
                  {serverListLocale?.alertMessages?.paymentPending}
                </Styles.AlertLink>
              )}
            </Styles.AlertText>
          </Styles.DangerLabel>
        </Styles.ListItemAlertDanger>
      )}

      {isStatusBuilding && (
        <Styles.ListItemAlertInfo data-testid={`server-list-item-alert-${server.id}`}>
          <Styles.InfoLabel>
            <IconInfo size="sm2" color="primaryDark" />

            {serverListLocale?.alertMessages?.serverBuilding}
          </Styles.InfoLabel>
        </Styles.ListItemAlertInfo>
      )}
    </Styles.ListRow>
  );
});

ServerListItem.displayName = 'ServerListItem';

export default ServerListItem;
